/**
 * Plugins — private-PyPI marketplace.
 *
 * Tabs:
 *   - Upload           publish a `.tar.gz` sdist to the private index
 *   - Capabilities     marketplace browse + install / upgrade / uninstall
 *   - Tools            marketplace browse + install / upgrade / uninstall
 *
 * Install state lives on each card (badge + action button). There is
 * no separate "Installed" tab — the `Installed` filter chip on each
 * marketplace grid is the cross-cutting view for what's currently
 * installed within that kind.
 *
 * Tab state is mirrored to ?tab= so deep-links survive.
 */
import { useMutation } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import { ApiError } from '@optum/api-client/client';
import { rollPluginsNow } from '@optum/api-client/endpoints/plugins';

import { useAuth } from '../../auth/AuthContext.js';
import { Tabs, type TabItem } from '../../ui/Tabs.js';
import { useToast } from '../../ui/useToast.js';
import { ListShape } from '../../ui/shapes.js';
import { MarketplaceGrid } from './MarketplaceGrid.js';
import { ServersTable } from './ServersTable.js';
import { UploadTab } from './UploadTab.js';
import { KeepQueryRedirect } from '../../components/shell/LegacyRedirect.js';

type TabKey = 'upload' | 'capabilities' | 'tools' | 'servers';

// Browse first, publish last: the catalog is read many times for every time
// something is pushed to it.
const TABS: TabItem<TabKey>[] = [
  { value: 'capabilities', label: 'Capabilities' },
  { value: 'tools', label: 'Tools' },
  { value: 'servers', label: 'Servers' },
  { value: 'upload', label: 'Upload' },
];

const TAB_VALUES: readonly TabKey[] = TABS.map((t) => t.value);

function readTab(value: string | null): TabKey {
  return (TAB_VALUES as readonly string[]).includes(value ?? '')
    ? (value as TabKey)
    : 'capabilities';
}

export function PluginsPage() {
  const { isPlatformAdmin } = useAuth();
  const [sp, setSp] = useSearchParams();
  const tab = readTab(sp.get('tab'));

  const toast = useToast();

  const roll = useMutation({
    mutationFn: rollPluginsNow,
    onSuccess: () => toast.success('Registry rolled'),
    onError: (e) => toast.error('Roll failed', e instanceof ApiError ? e.message : String(e)),
  });

  function setTab(next: TabKey) {
    const params = new URLSearchParams(sp);
    if (next === 'capabilities') params.delete('tab');
    else params.set('tab', next);
    setSp(params, { replace: true });
  }

  // The Templates tab moved to Automation's Triggers (Feature 74 D10).
  if (sp.get('tab') === 'templates') return <KeepQueryRedirect to="/automation?tab=triggers" />;

  return (
    <ListShape
      title="Plugins"
      subtitle={
        tab === 'upload'
          ? 'Publish source distributions to the private catalog. Installing them is a separate, deliberate step.'
          : tab === 'servers'
            ? 'One execution server per installed capability plugin. Tasks run on it without a cold start; a failed server keeps its tasks pending until it is back.'
            : 'Capabilities and tools on the platform. Each capability carries its own dependencies; tools are installed as part of the capabilities that declare them.'
      }
      menu={
        isPlatformAdmin
          ? [
              {
                icon: 'restart_alt',
                label: roll.isPending ? 'Rolling…' : 'Roll forward',
                sub: 'restart execution servers on the newest installs',
                disabled: roll.isPending,
                onSelect: () => roll.mutate(),
              },
            ]
          : undefined
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 22, paddingBottom: 64 }}>
        <Tabs items={TABS} value={tab} onChange={setTab} style={{ marginBottom: 16 }} />

        {tab === 'upload' && <UploadTab />}
        {tab === 'capabilities' && <MarketplaceGrid kind="capabilities" />}
        {tab === 'tools' && <MarketplaceGrid kind="tools" />}
        {tab === 'servers' && <ServersTable />}
      </div>
    </ListShape>
  );
}
